import type { TFunction } from 'i18next';
import { formatDate } from './formatDate';
import type {
  AvailabilityState,
  PeriodState,
  UnavailabilityPeriod,
} from '../api/unavailabilitiesApi';

/**
 * How an absence reads on screen — the one place, for the mobile screen and the four backoffice
 * spots alike. The states themselves are decided by Postgres (see `UserUnavailability`); nothing
 * here compares a date, it only puts words and separators around what the API sent.
 */

/**
 * A `YYYY-MM-DD` day, read as a local day. `new Date('2026-09-01')` is **UTC** midnight, which a
 * browser west of Greenwich renders as the 31st of August — the very one-day drift the `DATE`
 * column exists to avoid, re-introduced on the last mile.
 */
const formatDay = (isoDay: string): string => formatDate(`${isoDay}T00:00:00`);

/** `01/09/2026 → 15/09/2026`, the arrow both mock-ups use. Rendered in Geist Mono by its callers. */
export const formatPeriod = (startDate: string, endDate: string): string =>
  `${formatDay(startDate)} → ${formatDay(endDate)}`;

/**
 * A `switch` of string literals, never a key built by concatenating the state onto a prefix: the
 * repo's static guard requires a literal, and a concatenated key is invisible to the parity check.
 */
export const periodStateLabel = (t: TFunction, state: PeriodState): string => {
  switch (state) {
    case 'current':
      return t('Ongoing');
    case 'upcoming':
      return t('Upcoming');
    default:
      return t('Ended');
  }
};

/**
 * The technician's derived state. `upcoming` is **not** a third state of availability — he is
 * available today — which is why its wording says "soon" rather than plainly "unavailable".
 */
export const availabilityLabel = (t: TFunction, state: AvailabilityState): string => {
  switch (state) {
    case 'unavailable':
      return t('Unavailable');
    case 'upcoming':
      return t('Unavailable soon');
    default:
      return t('Available');
  }
};

/**
 * What the availability card and the sheet say beside the badge, in the three cases the design
 * draws.
 *
 * It branches on `availability` — the state the **server** derived, the same one the badge shows —
 * and only reads the periods to name the range. An earlier version re-derived the branch from the
 * list itself, which is a second derivation of the very thing this lot exists to derive once: the
 * badge and the sentence beside it could then disagree.
 */
export const availabilityHint = (
  t: TFunction,
  availability: AvailabilityState,
  periods: UnavailabilityPeriod[]
): string => {
  const range = (state: PeriodState): string | null => {
    const period = periods.find((candidate) => candidate.state === state);
    return period ? formatPeriod(period.startDate, period.endDate) : null;
  };

  if (availability === 'unavailable') {
    const period = range('current');
    // The state says he is away, so a period covers today — but a payload that disagreed must not
    // render "Unavailable from null".
    if (period) {
      return t(
        'Unavailable from {{period}}. You will not be proposed for a new intervention during this period, and your account stays active.',
        { period }
      );
    }
  }

  if (availability === 'upcoming') {
    const period = range('upcoming');
    if (period) return t('Next declared unavailability: {{period}}.', { period });
  }

  return t(
    'You are proposed for the interventions in your scope. Declare a period if you do not want to be asked.'
  );
};

/** The day count and the reason, when there is one — the sub-line of a period row. */
export const periodMeta = (t: TFunction, period: UnavailabilityPeriod): string => {
  const days = t('{{count}} day', { count: period.days });
  return period.reason ? `${days} · ${period.reason}` : days;
};
